<template>
  <div class="flex flex-1 flex-col">
    <Error404 v-if="error && error.status === 404" />
    <component
      v-else-if="story"
      :is="comps[story.content.component]"
      :blok="story.content"
    />

    <router-view v-slot="{ Component, route }">
      <transition name="page" mode="out-in">
        <component :is="Component" :key="route.path" />
      </transition>
    </router-view>
  </div>
</template>

<script setup lang="ts">
import {
  useStoryblokBridge,
  useStoryblokApi,
  type ISbStoryData,
  type ISbStory,
} from '@storyblok/vue';
import { TemplatePageStoryblok } from '@/types';
import { ISbPluginMetaFields } from '@/types';
import SbTemplatePage from '@/components/sb-templates/SbTemplatePage.vue';
import SbTemplateArticleOverview from '@/components/sb-templates/SbTemplateArticleOverview.vue';
import SbTemplateArticle from '@/components/sb-templates/SbTemplateArticle.vue';
import SbTemplateMapOverview from '@/components/sb-templates/SbTemplateMapOverview.vue';
import useConfig from '@/composables/useConfig';
import router from '@/router';
import { useRoute } from 'vue-router';

const comps = {
  // page templates
  TemplatePage: SbTemplatePage,
  // article templates
  TemplateArticleOverview: SbTemplateArticleOverview,
  TemplateArticle: SbTemplateArticle,
  // map templates
  TemplateMapOverview: SbTemplateMapOverview,
};

const route = useRoute();
const storyblokApi = useStoryblokApi();
const { brand, path, config } = useConfig();
const isEditor = import.meta.env.MODE.includes('editor');

type ErrorObj = {
  status: number;
  message: string;
  response: string;
};

const story = ref<ISbStoryData<TemplatePageStoryblok> | null>(null);
const error = ref<ErrorObj | null>(null);

try {
  // fetch page data
  let _path = path.value;

  // remove map location slug from path so we can fetch page data for the MapOverview page
  if (router.currentRoute.value.name === 'MapLocation') {
    _path = _path.replace(/\/map\/[^/]+/, '/map');
  }

  // make sure fetching works both on brand and editor sites
  let pathToStory = `cdn/stories/${brand.value}${_path}`;
  if (isEditor) {
    _path = _path === '/' ? '/home' : _path;
    pathToStory = `cdn/stories${_path}`;
  }

  const { data } = (await storyblokApi.get(pathToStory, {
    version: import.meta.env.VITE_STORYBLOK_VERSION,
    resolve_relations: 'Article.author',
  })) as ISbStory;

  story.value = data.story as ISbStoryData<TemplatePageStoryblok>;
} catch (err) {
  const errObj = JSON.parse(err as string);
  console.warn('errObj', errObj);
  error.value = errObj;
}

onMounted(() => {
  if (!story.value) return;
  useStoryblokBridge(story.value.id, (_story) => (story.value = _story));
});

const canonicalUrl = computed(() => {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const path = route.fullPath.endsWith('/')
    ? route.fullPath.slice(0, -1)
    : route.fullPath;
  return baseUrl + path;
});
const seo = computed(() => {
  const seo = story.value?.content.seo as ISbPluginMetaFields;
  return {
    title: seo?.title || config.value?.title,
    description: seo?.description || '',
    image: story.value?.content.seoImage?.filename || undefined,
  };
});
useHead({
  title: seo.value.title,
  meta: [
    { name: 'description', content: seo.value.description },
    { property: 'og:site_name', content: config.value?.title },
    { property: 'og:title', content: seo.value.title },
    { property: 'og:description', content: seo.value.description },
    { property: 'og:image', content: seo.value.image },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:type', content: 'website' },
    { property: 'twitter:title', content: seo.value.title },
    { property: 'twitter:description', content: seo.value.description },
    { property: 'twitter:image', content: seo.value.image },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }],
});
</script>
